import { Injectable } from "@angular/core"
import { TranslateService } from "@ngx-translate/core"
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  from,
  interval,
  take,
} from "rxjs"

import { TenantService } from "@app/cadastro/admin/services/tenant.service"
import { CurrentTenant } from "../models/CurrentTenant"
import { i18nLanguages } from "../constants/i18n-languages"
import { capitalize } from "lodash"

export type I18nModules = "backoffice"

export interface II18nLexParams {
  listParams?: Map<string, string>
}

@Injectable({
  providedIn: "root",
})
export class LexTranslateService {
  i18n: Record<string, string> = {}
  I18nLexParams: II18nLexParams = { listParams: new Map<string, string>() }
  translationFile: object
  optionsAdmConexia = []
  optionsAdmSchool = []
  currentTenant = new BehaviorSubject<CurrentTenant | null>(null)

  constructor(
    public translate: TranslateService,
    private tenantService: TenantService
  ) {}

  async loadTranslationsInFunctionOfTenant() {
    if (!this.tenantService.getCurrentTenant()) return

    const currentTenant: CurrentTenant = await firstValueFrom(
      this.tenantService.getCurrentTenant()
    )
    this.currentTenant.next(currentTenant)

    const tenantLanguage = i18nLanguages[currentTenant.language]

    await this.setTranslateLex(tenantLanguage, "backoffice")

    this.initSidebarOptions()
  }

  async setTranslateLex(lang: string, module: I18nModules) {
    if (!lang || !module) return

    const langFile = `${lang.toLowerCase()}/${module}`

    this.translationFile = await firstValueFrom(this.translate.use(langFile))
    await firstValueFrom((await this.waitForFile()).pipe(take(1)))
    this.setTranslationPage()
  }

  async setParams(key: string, val: string) {
    this.I18nLexParams.listParams.set(key, val)

    this.setTranslationPage()
  }

  setTranslationPage() {
    if (!this.translationFile) return
    Object.entries(this.translationFile).forEach(([key, value]) => {
      const params = this.getTranslationFileEntryParams(value as string)

      if (params.length) {
        const paramsObject = {}

        params.forEach((param) => {
          paramsObject[param] = this.I18nLexParams.listParams.get(param)
        })

        this.i18n[key] = this.translate.instant(key, paramsObject)
        return
      }

      this.i18n[key] = this.translate.instant(key)
    })
  }

  async waitForFile() {
    return from(interval(100)).pipe(
      filter(() => this.translationFile !== undefined)
    )
  }

  initSidebarOptions() {
    this.optionsAdmConexia = [
      {
        icon: "group",
        label: "Grupos Econômicos",
        link: "/grupos-economicos",
      },
      {
        icon: "domain",
        label: this.i18n["7624938c-6010-42a3-8ed9-d8bdd1e3d52b"],
        link: "/escolas",
      },
      {
        icon: "how_to_vote",
        label: "Combos",
        link: "/combos",
      },
      {
        icon: "vpn_key",
        label: "Licenças",
        link: "/licencas",
      },
      {
        icon: "label",
        label: "Tags",
        link: "/tags",
      },
      {
        icon: "library_books",
        label: capitalize(this.i18n["65c103a9-7145-4702-a027-3cb66880880b"]),
        link: "/disciplinas",
      },
      {
        icon: "warning",
        label: capitalize(this.i18n["8bbf47e5-6a27-4be8-a219-6bfa923fa7bc"]),
        link: "/ocorrencias",
      },
      {
        icon: "flag",
        label: "Marcas",
        link: "/marcas",
      },
      {
        icon: "device_hub",
        label: "Integrações",
        link: "/integracoes",
      },
      {
        icon: "local_mall",
        label: "Produtos",
        link: "/produtos",
      },
      {
        icon: "auto_stories",
        label: "Conteúdos",
        link: "/conteudos",
      },
      {
        icon: "person",
        label: capitalize(this.i18n["3354656e-5055-4c29-94f9-6c89dfe30d3f"]),
        link: "/usuarios",
      },
      {
        icon: "account_box",
        label: this.i18n["e845b518-3fce-443c-a26d-b8effe5f350a"],
        link: "/perfis",
      },
    ]

    this.optionsAdmSchool = [
      {
        icon: "receipt_long",
        label: capitalize("Relatórios"),
        link: "/relatorios",
      },
      {
        icon: "import_contacts",
        label: capitalize(this.i18n["c47bf1d3-c367-4234-853e-c75bb3bb12aa"]),
        link: "/cursos",
      },
      {
        icon: "group",
        label: capitalize(this.i18n["1211c23e-e05c-4720-ae40-5c8f6baaf536"]),
        link: "/turmas",
      },
      {
        icon: "library_books",
        label: capitalize(this.i18n["65c103a9-7145-4702-a027-3cb66880880b"]),
        link: "/disciplinas",
      },
      {
        icon: "person",
        label: capitalize(this.i18n["3354656e-5055-4c29-94f9-6c89dfe30d3f"]),
        link: "/usuarios",
      },
      {
        icon: "local_mall",
        label: capitalize(this.i18n["011b2d01-ca76-4864-ba10-5e70c29f434f"]),
        link: "/school/products",
      },
      {
        icon: "warning",
        label: capitalize(this.i18n["8bbf47e5-6a27-4be8-a219-6bfa923fa7bc"]),
        link: "/ocorrencias",
      },
      {
        icon: "domain",
        label: this.i18n["7624938c-6010-42a3-8ed9-d8bdd1e3d52b"],
        link: "/escolas",
      },
      {
        icon: "label",
        label: this.i18n["991527b3-a03f-46d2-b8c3-4aaacf8d1baf"] + "s",
        link: "/segmentos",
      },
      {
        icon: "meeting_room",
        label: this.i18n["301c3390-d075-4075-ab32-1b59f48312d0"],
        link: "/series",
      },
      {
        icon: "account_box",
        label: this.i18n["e845b518-3fce-443c-a26d-b8effe5f350a"],
        link: "/perfis",
      },
    ]
  }

  private getTranslationFileEntryParams(
    translationEntryValue: string
  ): string[] {
    return translationEntryValue
      .split("}}")
      .filter((value) => value.includes("{{"))
      .map((value) => value.split("{{")[1].trim())
  }
}
